/**
 * 크로스블록 선택 — 이벤트 컨트롤러(포인터 드래그 캡처).
 *
 * 각 블록의 `.content`가 독립 contenteditable 섬이라 네이티브 드래그는 시작 블록 안으로
 * 클램프된다. 이 컨트롤러는 마우스 드래그가 블록 경계를 넘는 순간 "크로스 모드"로 전환해
 * 선택을 직접 구동한다(기법 b: editorEl에 user-select:none + 매 이동 removeAllRanges +
 * 전면 커스텀 하이라이트). 캡처 로직은 이 파일에 격리되어, 기법 교체 시 여기만 바꾸면 된다.
 *
 * 동작:
 *   - 단일 블록 내 클릭/드래그 → 크로스 미진입(네이티브 그대로, 기존 툴바 빠른 경로 유지).
 *   - 블록 A→C 드래그 → 크로스 진입, (anchorIsland,offset)→(focusIsland,offset) 추적.
 *   - shift+클릭 → 기존 anchor 유지, 클릭 지점으로 focus 확장.
 *   - Escape/방향키(shift 없음) → 크로스 해제 + 캐럿 배치.
 *   - 새 mousedown(shift 없음)·에디터 밖 클릭 → 해제.
 *   - 표 셀/단/중첩 섬은 진입/커버 대상 아님(경계 클램프).
 *
 * v1: 선택 + 일괄 서식만. 선택 영역 편집(삭제/타이핑/붙여넣기)은 2단계.
 */

import { caretRangeFromPoint } from "./caret-nav.js";
import { caretToOffset, setSelection } from "../inline-format.js";
import {
  plainIslandOf,
  coveredIslands as computeCovered,
  paintCrossHighlight,
  clearCrossHighlight,
  crossBoundingRect,
} from "./cross-block-selection.js";

export function attachCrossBlockSelect(editorEl, opts) {
  opts = opts || {};
  const isEditable = typeof opts.isEditable === "function" ? opts.isEditable : () => true;
  const isComposing = typeof opts.isComposing === "function" ? opts.isComposing : () => false;
  const clearBlockSelect = typeof opts.clearBlockSelect === "function" ? opts.clearBlockSelect : () => {};
  const onSettled = typeof opts.onSettled === "function" ? opts.onSettled : () => {};

  let sel = null; // {anchorIsland, anchorOffset, focusIsland, focusOffset}
  let anchor = null; // {island, offset} — mousedown 지점
  let pointerDown = false;
  let engaged = false; // 크로스 모드(경계를 넘어 커스텀 구동 중)

  function covered() {
    return computeCovered(editorEl, sel);
  }
  function isActive() {
    return !!(sel && sel.anchorIsland && sel.focusIsland && sel.anchorIsland !== sel.focusIsland);
  }
  function isDragging() {
    return pointerDown && engaged;
  }

  function setUserSelectNone(on) {
    try {
      editorEl.style.userSelect = on ? "none" : "";
      editorEl.style.webkitUserSelect = on ? "none" : "";
    } catch (_) {}
  }

  function repaint() {
    paintCrossHighlight(covered());
  }

  function clear() {
    sel = null;
    anchor = null;
    engaged = false;
    pointerDown = false;
    setUserSelectNone(false);
    clearCrossHighlight();
  }

  function offsetAtPoint(x, y) {
    const rng = caretRangeFromPoint(x, y);
    if (!rng) return null;
    const island = plainIslandOf(rng.startContainer);
    if (!island) return null;
    return { island, offset: caretToOffset(island, rng.startContainer, rng.startOffset) };
  }

  // ── mousedown: anchor 기록(크로스 진입은 mousemove에서) ──
  function onMousedown(e) {
    if (!isEditable() || e.button !== 0 || isComposing()) return;
    // 표는 table-controls 소관 — 크로스 미관여.
    if (e.target.closest && e.target.closest(".lumir-tbl")) {
      if (isActive()) clear();
      return;
    }
    const at = offsetAtPoint(e.clientX, e.clientY);

    // shift+클릭: 기존 anchor 유지하고 focus만 이동(확장).
    if (e.shiftKey && (anchor || sel) && at) {
      const a = anchor || { island: sel.anchorIsland, offset: sel.anchorOffset };
      anchor = a;
      sel = { anchorIsland: a.island, anchorOffset: a.offset, focusIsland: at.island, focusOffset: at.offset };
      if (isActive()) {
        e.preventDefault();
        engaged = true;
        setUserSelectNone(true);
        const s = window.getSelection();
        if (s) s.removeAllRanges();
        repaint();
        onSettled();
      } else {
        // 같은 섬으로의 shift+클릭 → 네이티브 선택으로 환원.
        clearCrossHighlight();
        engaged = false;
        setUserSelectNone(false);
      }
      return;
    }

    // 일반 mousedown: 이전 크로스 해제 + 새 anchor.
    if (isActive() || engaged) {
      clearCrossHighlight();
      setUserSelectNone(false);
    }
    sel = null;
    engaged = false;
    clearBlockSelect();
    anchor = at; // 일반 섬이 아니면 null → 크로스 진입 불가(네이티브 처리)
    pointerDown = true;
  }

  // ── mousemove: 경계를 넘으면 크로스 진입 후 커스텀 구동 ──
  function onMousemove(e) {
    if (!pointerDown || !anchor) return;
    const at = offsetAtPoint(e.clientX, e.clientY);
    if (!at) return; // 표/단/중첩/여백 위 → 클램프(무시)
    if (!engaged && at.island === anchor.island) return; // 아직 같은 섬 → 네이티브 유지

    if (!engaged) {
      engaged = true;
      setUserSelectNone(true); // 네이티브 선택 확장 억제
    }
    sel = {
      anchorIsland: anchor.island,
      anchorOffset: anchor.offset,
      focusIsland: at.island,
      focusOffset: at.offset,
    };
    const s = window.getSelection();
    if (s) s.removeAllRanges();
    repaint();
  }

  // ── mouseup: 마무리(단일 섬으로 접히면 네이티브로 환원, 다중이면 크로스 확정) ──
  function onMouseup() {
    if (!pointerDown) return;
    pointerDown = false;
    if (!engaged) return; // 순수 네이티브(단일 섬) — 관여 없음

    setUserSelectNone(false);
    if (sel && sel.anchorIsland === sel.focusIsland) {
      // 한 섬으로 접힘 → 네이티브 단일 블록 선택으로 되돌려 기존 경로에 위임.
      clearCrossHighlight();
      try {
        sel.anchorIsland.focus();
        setSelection(sel.anchorIsland, sel.anchorOffset, sel.focusOffset);
      } catch (_) {}
      sel = null;
      engaged = false;
      return;
    }
    engaged = false;
    repaint();
    onSettled(); // 툴바 표시/위치
  }

  // ── keydown: Escape/방향키로 해제(크로스 활성 시) — caret-nav보다 먼저 등록됨 ──
  function placeCaret(island, offset) {
    if (!island) return;
    try {
      island.focus();
      setSelection(island, offset, offset);
    } catch (_) {}
  }
  function onKeydown(e) {
    if (!isActive()) return;
    const key = e.key;
    const cov = covered();
    const first = cov[0];
    const last = cov[cov.length - 1];
    if (key === "Escape") {
      e.preventDefault();
      e.stopImmediatePropagation();
      const a = { island: sel.anchorIsland, offset: sel.anchorOffset };
      clear();
      placeCaret(a.island, a.offset);
      return;
    }
    if (key === "ArrowLeft" || key === "ArrowUp") {
      e.preventDefault();
      e.stopImmediatePropagation();
      clear();
      if (first) placeCaret(first.island, first.start);
      return;
    }
    if (key === "ArrowRight" || key === "ArrowDown") {
      e.preventDefault();
      e.stopImmediatePropagation();
      clear();
      if (last) placeCaret(last.island, last.end);
      return;
    }
  }

  // 에디터 밖 클릭 시 해제(툴바 클릭은 제외 — 그래야 서식 적용이 유지된다).
  function onDocMousedown(e) {
    if (!isActive() && !engaged) return;
    if (editorEl.contains(e.target)) return; // 에디터 내부는 onMousedown이 처리
    if (
      e.target.closest &&
      e.target.closest(".lumir-format-toolbar, .lumir-floating-menu, .lumir-link-toolbar")
    )
      return; // 툴바 조작 유지
    clear();
  }

  editorEl.addEventListener("keydown", onKeydown);
  editorEl.addEventListener("mousedown", onMousedown);
  document.addEventListener("mousemove", onMousemove);
  document.addEventListener("mouseup", onMouseup);
  document.addEventListener("mousedown", onDocMousedown);

  return {
    isActive,
    isDragging,
    get: () => sel,
    clear,
    coveredIslands: covered,
    boundingRect: () => crossBoundingRect(covered()),
    repaint,
    destroy() {
      clear();
      editorEl.removeEventListener("keydown", onKeydown);
      editorEl.removeEventListener("mousedown", onMousedown);
      document.removeEventListener("mousemove", onMousemove);
      document.removeEventListener("mouseup", onMouseup);
      document.removeEventListener("mousedown", onDocMousedown);
    },
  };
}
